/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { createContext, useContext, type ReactNode } from "react";
import { useSOSEvent, type UseSOSEventResult } from "@/hooks/useSOSEvent";

const SOSEventContext = createContext<UseSOSEventResult | null>(null);

export function SOSEventProvider({ children }: { children: ReactNode }) {
  const value = useSOSEvent();
  return (
    <SOSEventContext.Provider value={value}>
      {children}
    </SOSEventContext.Provider>
  );
}

export function useSOSEventContext(): UseSOSEventResult {
  const ctx = useContext(SOSEventContext);
  if (!ctx) {
    throw new Error(
      "useSOSEventContext must be used within <SOSEventProvider>",
    );
  }
  return ctx;
}

/**
 * Safe variant — returns null when outside the provider. Used by components
 * like SOSButton that need to *optionally* see the SOS state without hard
 * failure when the user hasn't finished sign-in (provider isn't mounted yet).
 */
export function useOptionalSOSEvent(): UseSOSEventResult | null {
  return useContext(SOSEventContext);
}
